// Pure panel-tab model for the workbench — no store or runtime imports so it
// stays unit-testable. The WorkbenchStore composes these helpers.

// 'code' is opened programmatically (auto-opened by the streaming code viewer)
// and offered in the launcher; 'sub-agent' is opened programmatically from chat
// only — both are omitted from TOOLS_BY_PROJECT_TYPE below.
export type ToolType =
  | 'deployment'
  | 'analytics'
  | 'forms'
  | 'integrations'
  | 'payments'
  | 'shop'
  | 'funnels'
  | 'marketing'
  | 'reviews'
  | 'content'
  | 'automations'
  | 'sections'
  | 'marketplace'
  | 'domains'
  | 'assets'
  | 'code'
  | 'sub-agent';

export type ProjectType = 'landing';

export const TOOLS_BY_PROJECT_TYPE: Record<ProjectType, ToolType[]> = {
  landing: [
    'deployment',
    'analytics',
    'forms',
    'integrations',
    'payments',
    'shop',
    'funnels',
    'marketing',
    'reviews',
    'content',
    'automations',
    'sections',
    'marketplace',
    'domains',
    'assets',
  ],
};

export interface PanelTab {
  id: string;
  type: 'preview' | 'tool' | 'launcher';
  tool?: ToolType | null;
  label: string;
  closable: boolean;
}

export const TOOL_LABELS: Record<ToolType, string> = {
  deployment: 'Publish',
  analytics: 'Analytics',
  forms: 'Forms',
  integrations: 'Integrations',
  payments: 'Payments',
  shop: 'Shop',
  funnels: 'Funnels',
  marketing: 'Marketing',
  reviews: 'Reviews',
  content: 'Content',
  automations: 'Automations',
  sections: 'Sections',
  marketplace: 'Marketplace',
  domains: 'Domains',
  assets: 'Assets',
  code: 'Code',
  'sub-agent': 'Agent',
};

export const LAUNCHER_TAB_ID = 'launcher';

const DEFAULT_PREVIEW_TAB: PanelTab = {
  id: 'preview',
  type: 'preview',
  label: 'Preview',
  closable: false,
};

const LAUNCHER_TAB: PanelTab = {
  id: LAUNCHER_TAB_ID,
  type: 'launcher',
  label: 'Tools & files',
  closable: false,
};

// The bar starts minimal: Preview, the pinned Publish action, and the launcher.
// All other tools are opened on demand from the launcher as closable tabs.
export function buildFixedTabs(_projectType: ProjectType): PanelTab[] {
  return [
    DEFAULT_PREVIEW_TAB,
    { id: 'tool-deployment', type: 'tool', tool: 'deployment', label: TOOL_LABELS.deployment, closable: false },
    LAUNCHER_TAB,
  ];
}

// Insert a tool tab immediately before the launcher so the launcher stays last
// in the left tab group.
export function insertToolTab(tabs: PanelTab[], newTab: PanelTab): PanelTab[] {
  const launcherIdx = tabs.findIndex((t) => t.type === 'launcher');

  if (launcherIdx === -1) {
    return [...tabs, newTab];
  }

  return [...tabs.slice(0, launcherIdx), newTab, ...tabs.slice(launcherIdx)];
}
